/**
 * Feature Flags Zustand Store
 *
 * Manages all state for the Feature Flags section including:
 * - Feature flag list with filters and pagination
 * - CRUD operations for feature flags
 * - Toggle functionality with optimistic updates
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  FeatureFlag,
  FeatureFlagScope,
  PaginationMeta,
  FeatureFlagFilters,
  CreateFeatureFlagDto,
  UpdateFeatureFlagDto,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface FeatureFlagsError {
  code: string;
  message: string;
}

// ============================================
// Mock Data (until backend is ready)
// ============================================

const MOCK_FEATURE_FLAGS: FeatureFlag[] = [
  {
    id: '550e8400-e29b-41d4-a716-446655440010',
    name: 'New Dashboard',
    key: 'new_dashboard',
    description: 'Enable the redesigned dashboard experience with improved analytics',
    enabled: true,
    scope: 'global',
    targetPlans: null,
    targetTenants: null,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-20'),
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440011',
    name: 'AI Assistant',
    key: 'ai_assistant',
    description: 'Enable AI-powered form building assistant for enterprise plans',
    enabled: true,
    scope: 'plan',
    targetPlans: ['plan-enterprise', 'plan-business'],
    targetTenants: null,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-02-01'),
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440012',
    name: 'Beta Voice Features',
    key: 'beta_voice_features',
    description: 'Enable beta voice calling features for selected tenants',
    enabled: true,
    scope: 'tenant',
    targetPlans: null,
    targetTenants: ['tenant-acme', 'tenant-globex'],
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-02-15'),
    updatedAt: new Date('2024-03-01'),
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440013',
    name: 'Advanced Analytics',
    key: 'advanced_analytics',
    description: 'Enable advanced analytics and reporting features',
    enabled: false,
    scope: 'global',
    targetPlans: null,
    targetTenants: null,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-03-10'),
    updatedAt: new Date('2024-03-10'),
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440014',
    name: 'Document Collaboration',
    key: 'document_collaboration',
    description: 'Enable real-time document collaboration features',
    enabled: true,
    scope: 'plan',
    targetPlans: ['plan-enterprise'],
    targetTenants: null,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-04-01'),
    updatedAt: new Date('2024-04-15'),
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440015',
    name: 'Webhook V2',
    key: 'webhook_v2',
    description: 'Enable the new webhook system with retry logic',
    enabled: false,
    scope: 'global',
    targetPlans: null,
    targetTenants: null,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-05-01'),
    updatedAt: new Date('2024-05-01'),
  },
];

// ============================================
// State Interface
// ============================================

export interface FeatureFlagsState {
  // List
  flags: FeatureFlag[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: FeatureFlagsError | null;

  // Filters
  filters: FeatureFlagFilters;

  // Action States
  isCreating: boolean;
  isUpdating: boolean;
  isToggling: string | null; // flag id being toggled
  isDeleting: boolean;
  actionError: FeatureFlagsError | null;

  // Actions
  loadFlags: (page?: number) => Promise<void>;
  refreshFlags: () => Promise<void>;
  setFilter: <K extends keyof FeatureFlagFilters>(key: K, value: FeatureFlagFilters[K]) => void;
  clearFilters: () => void;
  createFlag: (dto: CreateFeatureFlagDto) => Promise<string | null>;
  updateFlag: (id: string, dto: UpdateFeatureFlagDto) => Promise<boolean>;
  toggleFlag: (id: string, enabled: boolean) => Promise<boolean>;
  deleteFlag: (id: string) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // List
  flags: [] as FeatureFlag[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as FeatureFlagsError | null,

  // Filters
  filters: {} as FeatureFlagFilters,

  // Action States
  isCreating: false,
  isUpdating: false,
  isToggling: null as string | null,
  isDeleting: false,
  actionError: null as FeatureFlagsError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useFeatureFlagsStore = create<FeatureFlagsState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadFlags: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      if (filters.search) params.set('search', filters.search);
      if (filters.scope) params.set('scope', filters.scope);
      if (filters.enabled !== undefined) params.set('enabled', String(filters.enabled));

      const response = await api.get<{
        success: boolean;
        data: {
          data: FeatureFlag[];
          pagination: PaginationMeta;
        };
      }>(`${API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.LIST}?${params.toString()}`);

      set({
        flags: response.data.data,
        pagination: response.data.pagination,
        isLoadingList: false,
      });
    } catch {
      // Use mock data when API is not available
      const { filters } = get();
      let filteredFlags = [...MOCK_FEATURE_FLAGS];

      if (filters.search) {
        const search = filters.search.toLowerCase();
        filteredFlags = filteredFlags.filter(
          (f) =>
            f.name.toLowerCase().includes(search) ||
            f.key.toLowerCase().includes(search) ||
            f.description?.toLowerCase().includes(search)
        );
      }

      if (filters.scope) {
        filteredFlags = filteredFlags.filter((f) => f.scope === filters.scope);
      }

      if (filters.enabled !== undefined) {
        filteredFlags = filteredFlags.filter((f) => f.enabled === filters.enabled);
      }

      set({
        flags: filteredFlags,
        pagination: {
          page: 1,
          limit: 20,
          total: filteredFlags.length,
          totalPages: 1,
          sortOrder: 'desc' as const,
        },
        isLoadingList: false,
      });
    }
  },

  refreshFlags: async () => {
    const { pagination } = get();
    await get().loadFlags(pagination?.page ?? 1);
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadFlags(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadFlags(1);
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createFlag: async (dto: CreateFeatureFlagDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<{
        success: boolean;
        data: FeatureFlag;
      }>(API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.CREATE, dto);

      set({ isCreating: false });
      await get().refreshFlags();
      return response.data.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create feature flag',
        },
      });
      return null;
    }
  },

  updateFlag: async (id: string, dto: UpdateFeatureFlagDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.UPDATE(id), dto);

      set({ isUpdating: false });
      await get().refreshFlags();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update feature flag',
        },
      });
      return false;
    }
  },

  toggleFlag: async (id: string, enabled: boolean): Promise<boolean> => {
    set({ isToggling: id, actionError: null });

    // Optimistic update
    set((state) => ({
      flags: state.flags.map((f) => (f.id === id ? { ...f, enabled } : f)),
    }));

    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.TOGGLE(id), { enabled });

      set({ isToggling: null });
      return true;
    } catch (error) {
      // Revert optimistic update
      set((state) => ({
        flags: state.flags.map((f) => (f.id === id ? { ...f, enabled: !enabled } : f)),
        isToggling: null,
        actionError: {
          code: 'TOGGLE_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to toggle feature flag',
        },
      }));
      return false;
    }
  },

  deleteFlag: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.DELETE(id));

      // Remove from local state
      set((state) => ({
        flags: state.flags.filter((f) => f.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete feature flag',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({
      listError: null,
      actionError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get count of enabled flags */
export const useEnabledFlagsCount = () => {
  return useFeatureFlagsStore((state) => state.flags.filter((f) => f.enabled).length);
};

/** Get flags grouped by scope */
export const useFlagsByScope = () => {
  return useFeatureFlagsStore((state) => {
    const byScope: Record<FeatureFlagScope, FeatureFlag[]> = {
      global: [],
      plan: [],
      tenant: [],
    };
    state.flags.forEach((f) => {
      byScope[f.scope].push(f);
    });
    return byScope;
  });
};

/** Get count of flags by scope */
export const useFlagCountByScope = (scope: FeatureFlagScope) => {
  return useFeatureFlagsStore((state) => state.flags.filter((f) => f.scope === scope).length);
};

/** Get only global flags */
export const useGlobalFlags = () => {
  return useFeatureFlagsStore((state) => state.flags.filter((f) => f.scope === 'global'));
};

/** Check if any feature flags data is loading */
export const useFeatureFlagsLoading = () => {
  const isLoadingList = useFeatureFlagsStore((state) => state.isLoadingList);
  const isCreating = useFeatureFlagsStore((state) => state.isCreating);
  const isUpdating = useFeatureFlagsStore((state) => state.isUpdating);
  const isToggling = useFeatureFlagsStore((state) => state.isToggling);
  const isDeleting = useFeatureFlagsStore((state) => state.isDeleting);
  return isLoadingList || isCreating || isUpdating || isToggling !== null || isDeleting;
};

/** Get active filters count */
export const useFeatureFlagFiltersCount = () => {
  return useFeatureFlagsStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.scope) count++;
    if (state.filters.enabled !== undefined) count++;
    return count;
  });
};
